import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import {
  defaultPageSize,
  defaultPagination,
} from 'src/app/shared/components/common/table/table.constants'
import { useSelector } from 'react-redux'
import { getDeferredConfig } from 'src/app/store/masterSetup'
import { IViewErrorPayload, IViewErrorResponse } from 'src/app/shared/models/issuedCheckProcessingLog.model'
import { getDetailIssuedCheckProcessing } from 'src/app/shared/services/IssuedCheckService'
import { useParams } from 'react-router-dom'
import { message } from 'antd'

import { ViewErrorContext } from './index.context'

const ViewErrorProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const params = useParams();
  const { id } = params;
  const majorConfig = useSelector(getDeferredConfig)

  // table pagination states
  const [currentPage, setCurrentPage] = useState<number>(defaultPagination)
  const [pageSize, setPageSize] = useState<number>(majorConfig?.systemSettings.recordsPerPage ?? defaultPageSize)
  const [total, setTotal] = useState<number>(0);
  const [rowData, setRowData] = useState<any>([])
  const totalRows = useMemo(() => total, [rowData?.length, total])
  const [viewErrorDetail, setViewErrorDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // table pagination methods
  const setCurrent = useCallback(
    async (page: number, pageSize: number) => {
      setCurrentPage(page)
      setPageSize(pageSize)
      if (id) {
        await handleSearchLogic({
          pageIndex: page,
          pageSize,
          id
        })
      }
    },
    [],
  )

  const onJumpToFirst = useCallback(async () => {
    await setCurrent(defaultPagination, pageSize)
  }, [pageSize, setCurrent])

  const onJumpToLast = useCallback(async () => {
    const lastPage = Math.ceil(totalRows / pageSize)
    await setCurrent(lastPage, pageSize)
  }, [pageSize, setCurrent, totalRows])

  const onPaginationChange = useCallback(
    async (page: number, pageSize: number) => {
      await setCurrent(page, pageSize)
    },
    [setCurrent],
  )

  const onPageSizeChange = useCallback(
    async (size: number) => {
      await setCurrent(defaultPagination, size)
    },
    [setCurrent],
  )
  const handleSearch = useCallback(async (payload: IViewErrorPayload) => {
    try {
      setIsLoading(true);
      const res = await getDetailIssuedCheckProcessing(payload)
      setViewErrorDetail(res.data ?? {
        fileName: "",
        inputDate: "",
        status: null,
        processedItems: "",
        rejectedItems: "",
        amount: "",
        data: []
      });
      setTotal(res.data?.total);
    } catch (error: any) {
      message.error(error?.error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearchLogic = useCallback(
    async (value: IViewErrorPayload) => {
      const searchData: IViewErrorPayload = {
        pageIndex: currentPage,
        pageSize: pageSize,
        ...value,
      }
      await handleSearch(searchData)
    },
    [
      currentPage,
      handleSearch,
      pageSize,
    ],
  )

  const getViewErrorDetail = async () => {
    if (id) {
      await handleSearchLogic({
        pageIndex: currentPage,
        pageSize,
        id
      })
    }
  }

  return (
    <ViewErrorContext.Provider
      value={{
        onJumpToFirst,
        onJumpToLast,
        onPaginationChange,
        onPageSizeChange,
        total,
        currentPage,
        pageSize,
        getViewErrorDetail,
        viewErrorDetail,
        isLoading
      }}
    >
      {children}
    </ViewErrorContext.Provider>
  )
}

export default ViewErrorProvider

